import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tables } from '@/integrations/supabase/types';
import { Staff } from './SchedulerTypes';
import { 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  TrendingUp, 
  TrendingDown, 
  Calendar,
  Download,
  Users,
  Target,
  ExternalLink
} from 'lucide-react';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import StaffPerformanceReview from './StaffPerformanceReview';

type StaffTodo = Tables<'staff_todos'>;

interface TaskCompletionReportProps {
  todos: StaffTodo[];
  staff: Staff[];
  projects?: Tables<'projects'>[];
}

interface TaskMetrics {
  staffId: string;
  staffName: string;
  totalTasks: number;
  completedTasks: number;
  onTimeTasks: number;
  lateTasks: number;
  pendingTasks: number;
  overdueTasks: number;
  averageDaysEarly: number;
  averageDaysLate: number;
  successRate: number;
}

interface TaskDetail {
  id: string;
  title: string;
  staffName: string;
  deadline: string | null;
  completedAt: string | null;
  status: string;
  priority: string;
  daysEarlyLate: number | null;
  completionStatus: 'on_time' | 'late' | 'pending' | 'overdue' | 'no_deadline';
}

const TaskCompletionReport: React.FC<TaskCompletionReportProps> = ({
  todos,
  staff,
  projects
}) => {
  const { toast } = useToast();
  const [viewMode, setViewMode] = useState<'summary' | 'detailed'>('summary');
  const [sortBy, setSortBy] = useState<'staff' | 'success_rate' | 'total'>('staff');
  const [selectedStaff, setSelectedStaff] = useState<{ id: string; name: string } | null>(null);
  const [showPerformanceReview, setShowPerformanceReview] = useState(false);

  // Calculate metrics for each staff member
  const staffMetrics = useMemo(() => {
    const metricsMap = new Map<string, TaskMetrics>();
    
    // Initialize metrics for all active staff
    staff.filter(s => s.status === 'active').forEach(s => {
      metricsMap.set(s.id, {
        staffId: s.id,
        staffName: s.name,
        totalTasks: 0,
        completedTasks: 0,
        onTimeTasks: 0,
        lateTasks: 0,
        pendingTasks: 0,
        overdueTasks: 0,
        averageDaysEarly: 0,
        averageDaysLate: 0,
        successRate: 0
      });
    });

    // Process todos
    todos.forEach(todo => {
      const metrics = metricsMap.get(todo.staff_id);
      if (!metrics) return;

      metrics.totalTasks++;

      if (todo.status === 'completed') {
        metrics.completedTasks++;
        
        if (todo.deadline && todo.completed_at) {
          const deadlineDate = new Date(todo.deadline);
          const completedDate = new Date(todo.completed_at);
          const daysDiff = Math.floor((deadlineDate.getTime() - completedDate.getTime()) / (1000 * 60 * 60 * 24));

          // Fix: Compare dates only, considering full deadline day (00:00-23:59:59) as on-time
          const deadlineDateOnly = new Date(deadlineDate);
          deadlineDateOnly.setHours(0, 0, 0, 0);
          const completedDateOnly = new Date(completedDate);
          completedDateOnly.setHours(0, 0, 0, 0);

          if (completedDateOnly <= deadlineDateOnly) {
            metrics.onTimeTasks++;
            metrics.averageDaysEarly += daysDiff;
          } else {
            metrics.lateTasks++;
            metrics.averageDaysLate += Math.abs(daysDiff);
          }
        } else if (!todo.deadline) {
          // Completed task without deadline counts as on-time
          metrics.onTimeTasks++;
        }
      } else {
        metrics.pendingTasks++;
        
        // Check if overdue
        if (todo.deadline) {
          const deadlineDate = new Date(todo.deadline);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          deadlineDate.setHours(0, 0, 0, 0);
          
          if (today > deadlineDate) {
            metrics.overdueTasks++;
          }
        }
      }
    });

    // Calculate averages and success rates
    metricsMap.forEach(metrics => {
      if (metrics.onTimeTasks > 0) {
        metrics.averageDaysEarly = Math.round(metrics.averageDaysEarly / metrics.onTimeTasks);
      }
      if (metrics.lateTasks > 0) {
        metrics.averageDaysLate = Math.round(metrics.averageDaysLate / metrics.lateTasks);
      }
      if (metrics.completedTasks > 0) {
        metrics.successRate = Math.round((metrics.onTimeTasks / metrics.completedTasks) * 100);
      }
    });

    return Array.from(metricsMap.values());
  }, [todos, staff]);

  // Calculate overall metrics
  const overallMetrics = useMemo(() => {
    return staffMetrics.reduce((acc, metrics) => ({
      totalTasks: acc.totalTasks + metrics.totalTasks,
      completedTasks: acc.completedTasks + metrics.completedTasks,
      onTimeTasks: acc.onTimeTasks + metrics.onTimeTasks,
      lateTasks: acc.lateTasks + metrics.lateTasks,
      pendingTasks: acc.pendingTasks + metrics.pendingTasks,
      overdueTasks: acc.overdueTasks + metrics.overdueTasks,
      successRate: 0
    }), {
      totalTasks: 0,
      completedTasks: 0,
      onTimeTasks: 0,
      lateTasks: 0,
      pendingTasks: 0,
      overdueTasks: 0,
      successRate: 0
    });
  }, [staffMetrics]);

  // Calculate overall success rate
  overallMetrics.successRate = overallMetrics.completedTasks > 0 
    ? Math.round((overallMetrics.onTimeTasks / overallMetrics.completedTasks) * 100)
    : 0;

  // Get detailed task list
  const taskDetails = useMemo(() => {
    return todos.map(todo => {
      const staffMember = staff.find(s => s.id === todo.staff_id);
      let completionStatus: TaskDetail['completionStatus'] = 'no_deadline';
      let daysEarlyLate: number | null = null;

      if (todo.deadline) {
        const deadlineDate = new Date(todo.deadline);

        if (todo.status === 'completed' && todo.completed_at) {
          const completedDate = new Date(todo.completed_at);
          daysEarlyLate = Math.floor((deadlineDate.getTime() - completedDate.getTime()) / (1000 * 60 * 60 * 24));

          // Fix: Compare dates only, considering full deadline day (00:00-23:59:59) as on-time
          const deadlineDateOnly = new Date(deadlineDate);
          deadlineDateOnly.setHours(0, 0, 0, 0);
          const completedDateOnly = new Date(completedDate);
          completedDateOnly.setHours(0, 0, 0, 0);

          completionStatus = completedDateOnly <= deadlineDateOnly ? 'on_time' : 'late';
        } else if (todo.status !== 'completed') {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          deadlineDate.setHours(0, 0, 0, 0);
          
          completionStatus = today > deadlineDate ? 'overdue' : 'pending';
        }
      } else if (todo.status === 'completed') {
        completionStatus = 'on_time';
      } else {
        completionStatus = 'pending';
      }

      return {
        id: todo.id,
        title: todo.title,
        staffName: staffMember?.name || 'Unknown',
        deadline: todo.deadline,
        completedAt: todo.completed_at,
        status: todo.status,
        priority: todo.priority,
        daysEarlyLate,
        completionStatus
      };
    });
  }, [todos, staff]);

  // Sort staff metrics
  const sortedMetrics = useMemo(() => {
    const sorted = [...staffMetrics];
    switch (sortBy) {
      case 'success_rate':
        return sorted.sort((a, b) => b.successRate - a.successRate);
      case 'total':
        return sorted.sort((a, b) => b.totalTasks - a.totalTasks);
      default:
        return sorted.sort((a, b) => a.staffName.localeCompare(b.staffName));
    }
  }, [staffMetrics, sortBy]);

  // Export to CSV
  const handleExport = () => {
    const csvHeaders = [
      'Staff Name',
      'Total Tasks',
      'Completed',
      'On Time',
      'Late',
      'Pending',
      'Overdue',
      'Success Rate (%)',
      'Avg Days Early',
      'Avg Days Late'
    ];

    const csvData = sortedMetrics.map(metrics => [
      metrics.staffName,
      metrics.totalTasks,
      metrics.completedTasks,
      metrics.onTimeTasks,
      metrics.lateTasks,
      metrics.pendingTasks,
      metrics.overdueTasks,
      metrics.successRate,
      metrics.averageDaysEarly,
      metrics.averageDaysLate
    ]);

    // Add overall summary row
    csvData.push([
      'TOTAL',
      overallMetrics.totalTasks,
      overallMetrics.completedTasks,
      overallMetrics.onTimeTasks,
      overallMetrics.lateTasks,
      overallMetrics.pendingTasks,
      overallMetrics.overdueTasks,
      overallMetrics.successRate,
      '',
      ''
    ]);

    const csvContent = [
      csvHeaders.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `task-completion-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: 'Export Successful',
      description: `Exported completion report for ${sortedMetrics.length} staff members`,
    });
  };

  const getCompletionStatusBadge = (status: TaskDetail['completionStatus']) => {
    switch (status) {
      case 'on_time':
        return <Badge className="bg-green-100 text-green-700">On Time</Badge>;
      case 'late':
        return <Badge className="bg-orange-100 text-orange-700">Late</Badge>;
      case 'overdue':
        return <Badge className="bg-red-100 text-red-700">Overdue</Badge>;
      case 'pending':
        return <Badge className="bg-blue-100 text-blue-700">Pending</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">No Deadline</Badge>;
    }
  };

  const getSuccessRateColor = (rate: number) => {
    if (rate >= 90) return 'text-green-600';
    if (rate >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const handleStaffClick = (staffId: string, staffName: string) => {
    setSelectedStaff({ id: staffId, name: staffName });
    setShowPerformanceReview(true);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Task Completion Report</h2>
        <div className="flex gap-2">
          <Select value={viewMode} onValueChange={(value) => setViewMode(value as 'summary' | 'detailed')}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="summary">Summary View</SelectItem>
              <SelectItem value="detailed">Detailed View</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleExport} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Overall Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4" />
              Total Tasks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overallMetrics.totalTasks}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              On Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{overallMetrics.onTimeTasks}</div>
            <p className="text-xs text-muted-foreground">
              {overallMetrics.completedTasks > 0 
                ? `${Math.round((overallMetrics.onTimeTasks / overallMetrics.completedTasks) * 100)}% of completed`
                : 'No completed tasks'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              Late
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{overallMetrics.lateTasks}</div>
            <p className="text-xs text-muted-foreground">
              {overallMetrics.completedTasks > 0 
                ? `${Math.round((overallMetrics.lateTasks / overallMetrics.completedTasks) * 100)}% of completed`
                : '-'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-red-600" />
              Overdue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{overallMetrics.overdueTasks}</div>
            <p className="text-xs text-muted-foreground">Currently overdue</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Success Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${getSuccessRateColor(overallMetrics.successRate)}`}>
              {overallMetrics.successRate}%
            </div>
            <p className="text-xs text-muted-foreground">On-time completion</p>
          </CardContent>
        </Card>
      </div>

      {/* View Content */}
      {viewMode === 'summary' ? (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Staff Performance Summary</CardTitle>
              <Select value={sortBy} onValueChange={(value) => setSortBy(value as 'staff' | 'success_rate' | 'total')}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">By Name</SelectItem>
                  <SelectItem value="success_rate">By Success Rate</SelectItem>
                  <SelectItem value="total">By Total Tasks</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff</TableHead>
                  <TableHead className="text-center">Total</TableHead>
                  <TableHead className="text-center">Completed</TableHead>
                  <TableHead className="text-center">On Time</TableHead>
                  <TableHead className="text-center">Late</TableHead>
                  <TableHead className="text-center">Pending</TableHead>
                  <TableHead className="text-center">Overdue</TableHead>
                  <TableHead className="text-center">Success Rate</TableHead>
                  <TableHead className="text-center">Avg Days Early</TableHead>
                  <TableHead className="text-center">Avg Days Late</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedMetrics.map(metrics => (
                  <TableRow key={metrics.staffId}>
                    <TableCell>
                      <Button
                        variant="link"
                        className="p-0 h-auto font-medium text-left justify-start"
                        onClick={() => handleStaffClick(metrics.staffId, metrics.staffName)}
                      >
                        {metrics.staffName}
                        <ExternalLink className="ml-1 h-3 w-3" />
                      </Button>
                    </TableCell>
                    <TableCell className="text-center">{metrics.totalTasks}</TableCell>
                    <TableCell className="text-center">{metrics.completedTasks}</TableCell>
                    <TableCell className="text-center text-green-600">{metrics.onTimeTasks}</TableCell>
                    <TableCell className="text-center text-orange-600">{metrics.lateTasks}</TableCell>
                    <TableCell className="text-center">{metrics.pendingTasks}</TableCell>
                    <TableCell className="text-center text-red-600">{metrics.overdueTasks}</TableCell>
                    <TableCell className="text-center">
                      <span className={`font-bold ${getSuccessRateColor(metrics.successRate)}`}>
                        {metrics.successRate}%
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {metrics.averageDaysEarly > 0 ? `${metrics.averageDaysEarly}d` : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      {metrics.averageDaysLate > 0 ? `${metrics.averageDaysLate}d` : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Detailed Task Report</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Task</TableHead>
                  <TableHead>Staff</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Deadline</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Days Early/Late</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {taskDetails.map(task => (
                  <TableRow key={task.id}>
                    <TableCell className="font-medium max-w-xs truncate">{task.title}</TableCell>
                    <TableCell>{task.staffName}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${
                        task.priority === 'urgent' ? 'text-purple-700' :
                        task.priority === 'high' ? 'text-red-700' :
                        task.priority === 'medium' ? 'text-yellow-700' :
                        'text-green-700'
                      }`}>
                        {task.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {task.deadline ? format(new Date(task.deadline), 'MMM d, yyyy') : '-'}
                    </TableCell>
                    <TableCell>
                      {task.completedAt ? format(new Date(task.completedAt), 'MMM d, yyyy') : '-'}
                    </TableCell>
                    <TableCell>{getCompletionStatusBadge(task.completionStatus)}</TableCell>
                    <TableCell className="text-center">
                      {task.daysEarlyLate !== null ? (
                        <span className={task.daysEarlyLate >= 0 ? 'text-green-600' : 'text-orange-600'}>
                          {task.daysEarlyLate >= 0 ? `${task.daysEarlyLate}d early` : `${Math.abs(task.daysEarlyLate)}d late`}
                        </span>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Staff Performance Review Modal */}
      {selectedStaff && (
        <StaffPerformanceReview
          isOpen={showPerformanceReview}
          onClose={() => {
            setShowPerformanceReview(false);
            setSelectedStaff(null);
          }}
          staffId={selectedStaff.id}
          staffName={selectedStaff.name}
          todos={todos}
        />
      )}
    </div>
  );
};

export default TaskCompletionReport;